import { useEffect, useState, useMemo, useCallback } from "react";
import {
  Plus,
  DownloadSimple,
  PencilSimple,
  Trash,
  CheckCircle,
  X,
  ArrowsClockwise,
  Sparkle,
  MagnifyingGlass,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { API, formatEUR, formatDate } from "../lib/api";
import { MESES_ES } from "@/lib/remesa";
import { useAuth } from "@/context/AuthContext";
import { PERMISSIONS } from "@/lib/permissions";
import { CONCEPTO_LABEL, TIPOS } from "@/constants/recibos";

const PAGE_SIZE = 50;

const todayYM = () => {
  const d = new Date();

  return {
    anio: d.getFullYear(),
    mes: d.getMonth() + 1,
  };
};

const emptyRecibo = () => ({
  fallero_id: "",
  conceptos: [],
  fecha_emision: new Date().toISOString().slice(0, 10),
  fecha_pago: "",
  estado: "pendiente",
  notas: "",
});

const ESTADO_STYLE = {
  pagado: "bg-[#D1FAE5] text-[#065F46]",
  pendiente: "bg-[#FFEDD5] text-[#9A3412]",
  enviado: "bg-[#E5E7EB] text-[#374151]",
};

const totalConceptos = (cs = []) =>
  cs.reduce((s, c) => s + (Number(c.importe) || 0), 0);

export default function Recibos() {
  const [recibos, setRecibos] = useState([]);
  const [falleros, setFalleros] = useState([]);
  const [filterEstado, setFilterEstado] = useState("");
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);
  const { can, hasModule } = useAuth();
  const canManage = can(PERMISSIONS.MANAGE_RECIBOS);
  const [sortBy, setSortBy] = useState("fecha_pago");
  const [sortDir, setSortDir] = useState("desc");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [view, setView] = useState("recibos");
  const [pagosManuales, setPagosManuales] = useState([]);
  const [pagoModal, setPagoModal] = useState(false);
  const [selectedMandato, setSelectedMandato] = useState(null);
  const [pagoForm, setPagoForm] = useState({
    importe: "",
    fecha: new Date().toISOString().slice(0, 10),
    notas: "",
  });
  const [historialModal, setHistorialModal] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [loadingHistorial, setLoadingHistorial] = useState(false);
  const [loadingPagosManuales, setLoadingPagosManuales] = useState(true);
  const [modalGen, setModalGen] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);

      const recibosReq = api.get("/recibos", {
        params: {
          estado: filterEstado || undefined,
        },
      });

      const fallerosReq =
        modal && can(PERMISSIONS.VIEW_MEMBERS)
          ? api.get("/falleros")
          : Promise.resolve({ data: [] });

      const [r, f] = await Promise.all([recibosReq, fallerosReq]);

      setRecibos(r.data);

      if (f.data.length) {
        setFalleros(f.data);
      }
    } catch {
      toast.error("No se pudieron cargar los recibos");
    } finally {
      setLoading(false);
    }
  }, [filterEstado, modal, can]);

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line
  }, [fetchAll]);

  const loadPagos = useCallback(async () => {
    try {
      setLoadingPagosManuales(true);
      const res = await api.get("/recibos/pagos-manuales");
      setPagosManuales(res.data);
    } catch {
      toast.error("No se pudieron cargar los pagos manuales");
    } finally {
      setLoadingPagosManuales(false);
    }
  }, []);

  useEffect(() => {
    if (view !== "pagos-manuales") return;
    loadPagos();
  }, [view, loadPagos]);

  useEffect(() => {
    setPage(1);
  }, [search, filterEstado]);

  const filteredRecibos = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) {
      return recibos;
    }

    return recibos.filter((r) => {
      return (
        (r.numero || "").toLowerCase().includes(q) ||
        (r.fallero_nombre || "").toLowerCase().includes(q) ||
        (r.estado || "").toLowerCase().includes(q) ||
        (r.notas || "").toLowerCase().includes(q) ||
        (r.conceptos || []).some(
          (c) =>
            (c.descripcion || "").toLowerCase().includes(q) ||
            (CONCEPTO_LABEL[c.tipo] || c.tipo || "").toLowerCase().includes(q),
        )
      );
    });
  }, [recibos, search]);

  const sortedRecibos = useMemo(() => {
    return [...filteredRecibos].sort((a, b) => {
      let av;
      let bv;

      switch (sortBy) {
        case "fallero":
          av = (a.fallero_nombre || "").toLowerCase();

          bv = (b.fallero_nombre || "").toLowerCase();
          break;

        case "importe":
          av = Number(a.importe || 0);
          bv = Number(b.importe || 0);
          break;

        case "estado":
          av = a.estado || "";
          bv = b.estado || "";
          break;

        case "fecha_pago":
        default:
          av = a.fecha_pago || "";
          bv = b.fecha_pago || "";
          break;
      }

      if (av < bv) {
        return sortDir === "asc" ? -1 : 1;
      }

      if (av > bv) {
        return sortDir === "asc" ? 1 : -1;
      }

      return 0;
    });
  }, [filteredRecibos, sortBy, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sortedRecibos.length / PAGE_SIZE));

  const paginatedRecibos = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;

    return sortedRecibos.slice(start, start + PAGE_SIZE);
  }, [sortedRecibos, page]);

  const loadDefaults = async (fallero_id, fecha) => {
    if (!fallero_id) return [];
    try {
      const { data } = await api.get("/recibos/conceptos-default", {
        params: { fallero_id, fecha },
      });
      return data.conceptos || [];
    } catch {
      return [];
    }
  };

  const onChangeFallero = async (fallero_id) => {
    const conceptos = await loadDefaults(fallero_id, modal.data.fecha_pago);
    setModal({
      ...modal,
      data: { ...modal.data, fallero_id, conceptos },
    });
  };

  const onChangeFechaPago = async (fecha_pago) => {
    if (modal.data.fallero_id && !modal.isEdit) {
      const conceptos = await loadDefaults(modal.data.fallero_id, fecha_pago);
      setModal({
        ...modal,
        data: { ...modal.data, fecha_pago, conceptos },
      });
    } else {
      setModal({ ...modal, data: { ...modal.data, fecha_pago } });
    }
  };

  const recomputeDefaults = async () => {
    const conceptos = await loadDefaults(
      modal.data.fallero_id,
      modal.data.fecha_pago,
    );
    setModal({ ...modal, data: { ...modal.data, conceptos } });
    toast.success("Conceptos recalculados");
  };

  const updateConcepto = (idx, key, value) => {
    const next = [...modal.data.conceptos];
    next[idx] = { ...next[idx], [key]: value };
    setModal({ ...modal, data: { ...modal.data, conceptos: next } });
  };

  const removeConcepto = (idx) => {
    const next = modal.data.conceptos.filter((_, i) => i !== idx);
    setModal({ ...modal, data: { ...modal.data, conceptos: next } });
  };

  const addConcepto = () => {
    const usados = new Set(modal.data.conceptos.map((c) => c.tipo));
    const tipo = TIPOS.find((t) => !usados.has(t)) || "cuota";
    setModal({
      ...modal,
      data: {
        ...modal.data,
        conceptos: [
          ...modal.data.conceptos,
          { tipo, importe: 0, descripcion: "" },
        ],
      },
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!modal.data.fallero_id) {
      toast.error("Selecciona un fallero");
      return;
    }
    if (!modal.data.conceptos.length) {
      toast.error("Añade al menos un concepto");
      return;
    }
    try {
      const payload = {
        ...modal.data,
        conceptos: modal.data.conceptos.map((c) => ({
          tipo: c.tipo,
          importe: Number(c.importe) || 0,
          descripcion: c.descripcion || "",
        })),
        fecha_pago: modal.data.fecha_pago || null,
      };
      if (modal.isEdit) {
        await api.put(`/recibos/${modal.data.id}`, payload);
        toast.success("Recibo actualizado");
      } else {
        await api.post("/recibos", payload);
        toast.success("Recibo creado");
      }
      setModal(null);
      fetchAll();
    } catch (err) {
      toast.error("Error: " + (err?.response?.data?.detail || err.message));
    }
  };

  const markPaid = async (r) => {
    await api.put(`/recibos/${r.id}`, {
      fallero_id: r.fallero_id,
      conceptos: r.conceptos || [],
      fecha_pago: new Date().toISOString().slice(0, 10),
      estado: "pagado",
      notas: r.notas || "",
      remesa_id: r.remesa_id || null,
    });
    toast.success("Marcado como pagado");
    fetchAll();
  };

  const onDelete = async (r) => {
    if (!window.confirm(`¿Eliminar recibo ${r.numero}?`)) return;
    await api.delete(`/recibos/${r.id}`);
    toast.success("Eliminado");
    fetchAll();
  };

  const downloadPdf = async (r) => {
    try {
      const token = localStorage.getItem("local_token");
      const res = await fetch(`${API}/recibos/${r.id}/pdf`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("PDF error");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `recibo-${r.numero}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("No se pudo descargar el PDF");
    }
  };

  const openNew = async () => {
    if (falleros.length === 0 && can(PERMISSIONS.VIEW_MEMBERS)) {
      try {
        const res = await api.get("/falleros");

        setFalleros(res.data);
      } catch {
        toast.error("No se pudieron cargar los falleros");
      }
    }

    setModal({
      data: emptyRecibo(),
      isEdit: false,
    });
  };

  const openGenerarMes = () => {
    const { anio, mes } = todayYM();

    setModalGen({ anio, mes });
  };

  const confirmGenerarMes = async () => {
    if (!modalGen) {
      return;
    }

    try {
      const { data } = await api.post("/recibos/generar-mes", {
        anio: modalGen.anio,
        mes: modalGen.mes,
      });

      toast.success(
        `${data.creados} recibo${data.creados === 1 ? "" : "s"} creado${
          data.creados === 1 ? "" : "s"
        }`,
      );

      setModalGen(null);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  };

  const generarEjercicio = async () => {
    if (
      !window.confirm(
        "Se generarán TODOS los recibos del ejercicio fallero completo (abril-marzo). Solo se crearán los que no existan todavía. ¿Continuar?",
      )
    )
      return;

    try {
      const { data } = await api.post("/recibos/generar-ejercicio");

      toast.success(
        `${data.total_creados} recibo${
          data.total_creados === 1 ? "" : "s"
        } generado${data.total_creados === 1 ? "" : "s"} para el ejercicio ${
          data.ejercicio
        }`,
      );

      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  };

  const submitPagoManual = async (e) => {
    e.preventDefault();

    if (!selectedMandato) {
      return;
    }

    try {
      await api.post("/recibos/pagos-manuales", {
        mandato_id: selectedMandato.mandato_id,
        importe: Number(pagoForm.importe),
        fecha: pagoForm.fecha,
        notas: pagoForm.notas,
      });

      toast.success("Pago registrado");

      setPagoModal(false);
      setSelectedMandato(null);
      setPagoForm({
        importe: "",
        fecha: new Date().toISOString().slice(0, 10),
        notas: "",
      });
      fetchAll();
      await loadPagos();
    } catch (err) {
      toast.error(
        typeof err?.response?.data?.detail === "string"
          ? err.response.data.detail
          : err?.response?.data?.detail?.[0]?.msg ||
              "No se pudo registrar el pago",
      );
    }
  };

  const aplicarSaldosPendientes = async () => {
    try {
      const { data } = await api.post("/recibos/pagos-manuales/aplicar-saldos");
      toast.success(
        `Se han pagado automáticamente ${data.recibos_pagados} recibo${data.recibos_pagados === 1 ? "" : "s"}`,
      );
      fetchAll();
      await loadPagos();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "No se pudieron aplicar los saldos",
      );
    }
  };

  const openEdit = async (r) => {
    if (falleros.length === 0 && can(PERMISSIONS.VIEW_MEMBERS)) {
      try {
        const res = await api.get("/falleros");

        setFalleros(res.data);
      } catch {
        toast.error("No se pudieron cargar los falleros");
      }
    }
    setModal({
      data: {
        ...r,
        fecha_pago: r.fecha_pago || "",
        conceptos: r.conceptos || [],
      },
      isEdit: true,
    });
  };

  const openHistorial = async (mandato) => {
    try {
      setSelectedMandato(mandato);
      setHistorialModal(true);
      setLoadingHistorial(true);

      const res = await api.get(
        `/recibos/pagos-manuales/${mandato.mandato_id}/historial`,
      );

      setHistorial(res.data);
    } catch {
      toast.error("No se pudo cargar el historial");
    } finally {
      setLoadingHistorial(false);
    }
  };

  return (
    <div className="p-6 md:p-10" data-testid="recibos-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-med-muted font-semibold mb-2">
            Cobros
          </div>
          <h1 className="font-display text-4xl font-bold text-med-text">
            Recibos
          </h1>
          {canManage && (
            <p className="text-med-muted mt-1">
              Recibos compuestos por conceptos. Al elegir un fallero se
              autocargan según su categoría y mandato.
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {canManage && (
            <>
              <button
                onClick={openGenerarMes}
                data-testid="generar-mes-btn"
                className="inline-flex items-center gap-2 bg-med-secondary hover:bg-med-secondary-hover disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl px-5 py-2.5 font-semibold transition-colors active:scale-[0.98]"
              >
                <Sparkle size={18} weight="duotone" />
                Generar recibos del mes
              </button>
              <button
                onClick={generarEjercicio}
                data-testid="generar-ejercicio-btn"
                className="inline-flex items-center gap-2 bg-med-warning hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl px-5 py-2.5 font-semibold transition-colors active:scale-[0.98]"
              >
                <Sparkle size={18} weight="duotone" />
                Generar ejercicio completo
              </button>
              <button
                onClick={aplicarSaldosPendientes}
                className="inline-flex items-center gap-2 bg-med-surface border border-med-border hover:border-med-secondary text-med-text rounded-xl px-5 py-2.5 font-semibold transition-colors"
              >
                <ArrowsClockwise size={18} weight="duotone" />
                Aplicar saldos pendientes
              </button>
              <button
                onClick={openNew}
                data-testid="add-recibo-btn"
                className="inline-flex items-center gap-2 bg-med-primary hover:bg-med-primary-hover disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl px-5 py-2.5 font-semibold transition-colors active:scale-[0.98]"
              >
                <Plus size={18} weight="bold" />
                Nuevo recibo
              </button>
            </>
          )}
        </div>
      </header>

      <div className="flex flex-wrap gap-2 mb-6">
        {[
          {
            key: "",
            label: "Todos",
          },
          {
            key: "pendiente",
            label: "Pendientes",
          },
          {
            key: "pagado",
            label: "Pagados",
          },
          {
            key: "pagos-manuales",
            label: "Pagos manuales",
            perm: PERMISSIONS.MANAGE_RECIBOS,
          },
        ]
          .filter((s) => {
            if (!s.perm) {
              return true;
            }

            return can(s.perm);
          })
          .map((s) => (
            <button
              key={s.key || "all"}
              onClick={() => {
                if (s.key === "pagos-manuales") {
                  setView("pagos-manuales");
                } else {
                  setView("recibos");
                  setFilterEstado(s.key);
                }
              }}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                (view === "pagos-manuales" && s.key === "pagos-manuales") ||
                (view === "recibos" && filterEstado === s.key)
                  ? "bg-med-secondary text-white"
                  : "bg-med-surface border border-med-border text-med-text hover:border-med-secondary"
              }`}
            >
              {s.label}
            </button>
          ))}
      </div>

      <div className="relative flex-1 mb-6">
        <MagnifyingGlass
          size={18}
          className="absolute left-3.5 top-1/2 -translate-y-1/2 text-med-muted"
        />
        <input
          type="text"
          placeholder="Buscar recibo, fallero, concepto..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="search-input"
          className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-med-surface border border-med-border focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 outline-none transition-all"
        />
      </div>

      {view === "recibos" && (
        <div className="bg-med-surface border border-med-border rounded-2xl overflow-hidden">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-med-surface2 text-xs uppercase tracking-[0.1em] text-med-muted font-semibold">
                <th className="px-5 py-3.5">Nº</th>

                <th
                  onClick={() => {
                    if (sortBy === "fallero") {
                      setSortDir(sortDir === "asc" ? "desc" : "asc");
                    } else {
                      setSortBy("fallero");
                      setSortDir("asc");
                    }
                  }}
                  className="px-5 py-3.5 cursor-pointer select-none hover:text-med-primary transition-colors"
                >
                  Fallero
                </th>

                <th className="px-5 py-3.5">Conceptos</th>

                <th
                  onClick={() => {
                    if (sortBy === "fecha_pago") {
                      setSortDir(sortDir === "asc" ? "desc" : "asc");
                    } else {
                      setSortBy("fecha_pago");
                      setSortDir("desc");
                    }
                  }}
                  className="px-5 py-3.5 cursor-pointer select-none hover:text-med-primary transition-colors"
                >
                  Fecha de pago
                </th>

                <th
                  onClick={() => {
                    if (sortBy === "importe") {
                      setSortDir(sortDir === "asc" ? "desc" : "asc");
                    } else {
                      setSortBy("importe");
                      setSortDir("desc");
                    }
                  }}
                  className="px-5 py-3.5 cursor-pointer select-none hover:text-med-primary transition-colors"
                >
                  Total
                </th>

                <th
                  onClick={() => {
                    if (sortBy === "estado") {
                      setSortDir(sortDir === "asc" ? "desc" : "asc");
                    } else {
                      setSortBy("estado");
                      setSortDir("asc");
                    }
                  }}
                  className="px-5 py-3.5 cursor-pointer select-none hover:text-med-primary transition-colors"
                >
                  Estado
                </th>

                <th className="px-5 py-3.5 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-5 py-16 text-center text-med-muted"
                  >
                    Cargando recibos...
                  </td>
                </tr>
              ) : recibos.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-5 py-16 text-center text-med-muted"
                  >
                    No hay recibos. Crea el primero.
                  </td>
                </tr>
              ) : (
                paginatedRecibos.map((r) => (
                  <tr
                    key={r.id}
                    className="border-t border-med-border/60 hover:bg-med-surface2/60"
                    data-testid={`recibo-row-${r.id}`}
                  >
                    <td className="px-5 py-4 font-mono text-sm text-med-secondary">
                      {r.numero}
                    </td>
                    <td className="px-5 py-4 font-medium text-med-text">
                      {r.fallero_nombre}
                    </td>
                    <td className="px-5 py-4 text-sm">
                      <div className="flex flex-wrap gap-1">
                        {(r.conceptos || []).slice(0, 3).map((c, i) => (
                          <span
                            key={i}
                            className="px-1.5 py-0.5 rounded bg-med-surface2 text-[10px] uppercase tracking-wide text-med-muted font-semibold"
                          >
                            {CONCEPTO_LABEL[c.tipo] || c.tipo}
                          </span>
                        ))}
                        {(r.conceptos || []).length > 3 && (
                          <span className="text-[10px] text-med-muted">
                            +{r.conceptos.length - 3}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-sm text-med-muted">
                      {formatDate(r.fecha_pago)}
                    </td>
                    <td className="px-5 py-4 font-semibold text-med-text">
                      {formatEUR(r.importe)}
                    </td>
                    <td className="px-5 py-4">
                      <span
                        className={`px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${ESTADO_STYLE[r.estado]}`}
                      >
                        {r.estado}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <div className="inline-flex gap-1.5">
                        {canManage && r.estado !== "pagado" && (
                          <button
                            onClick={() => markPaid(r)}
                            data-testid={`mark-paid-${r.id}`}
                            title="Marcar pagado"
                            className="p-2 rounded-lg hover:bg-med-success/10 text-med-success"
                          >
                            <CheckCircle size={18} weight="duotone" />
                          </button>
                        )}
                        <button
                          onClick={() => downloadPdf(r)}
                          data-testid={`pdf-${r.id}`}
                          title="Descargar PDF"
                          className="p-2 rounded-lg hover:bg-med-surface2 text-med-secondary"
                        >
                          <DownloadSimple size={18} weight="duotone" />
                        </button>
                        {canManage && r.estado === "pendiente" && (
                          <>
                            <button
                              onClick={() => openEdit(r)}
                              data-testid={`edit-recibo-${r.id}`}
                              className="p-2 rounded-lg hover:bg-med-surface2 text-med-secondary"
                            >
                              <PencilSimple size={18} weight="duotone" />
                            </button>
                            <button
                              onClick={() => onDelete(r)}
                              data-testid={`delete-recibo-${r.id}`}
                              className="p-2 rounded-lg hover:bg-med-danger/10 text-med-danger"
                            >
                              <Trash size={18} weight="duotone" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {view === "recibos" && totalPages > 1 && (
        <div className="mt-6 flex items-center justify-center gap-2">
          <button
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
            className="rounded-xl border border-med-border px-4 py-2 text-sm disabled:opacity-40"
          >
            Anterior
          </button>

          <div className="text-sm text-med-muted">
            Página {page} de {totalPages}
          </div>

          <button
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}
            className="rounded-xl border border-med-border px-4 py-2 text-sm disabled:opacity-40"
          >
            Siguiente
          </button>
        </div>
      )}

      {canManage && view === "pagos-manuales" && (
        <div className="space-y-5">
          {loadingPagosManuales ? (
            <div className="rounded-2xl border border-dashed border-med-border bg-med-surface p-10 text-center">
              <h3 className="text-lg font-semibold text-med-text">
                Cargando mandatos de pago en mano...
              </h3>
            </div>
          ) : (
            pagosManuales.length === 0 && (
              <div className="rounded-2xl border border-dashed border-med-border bg-med-surface p-10 text-center">
                <h3 className="text-lg font-semibold text-med-text">
                  No hay mandatos disponibles
                </h3>

                <p className="mt-2 text-sm text-med-muted">
                  Los pagos manuales aparecerán aquí cuando existan mandatos y
                  recibos asociados.
                </p>
              </div>
            )
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {pagosManuales.map((m) => (
              <div
                key={m.mandato_id}
                className="rounded-2xl border border-med-border bg-med-surface p-5"
              >
                <div>
                  <div className="text-xs uppercase tracking-wide text-med-muted">
                    Mandato
                  </div>

                  <h3 className="mt-1 text-lg font-bold text-med-text">
                    {m.referencia} · {m.mandatario}
                  </h3>
                </div>

                <div className="mt-5 space-y-3">
                  <div className="rounded-xl bg-med-bg px-4 py-3">
                    <div className="text-xs uppercase tracking-wide text-med-muted">
                      Saldo disponible
                    </div>

                    <div
                      className={`mt-1 text-2xl font-bold ${
                        Number(m.saldo) > 0 ? "text-green-600" : "text-med-text"
                      }`}
                    >
                      {formatEUR(Number(m.saldo))}
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <span className="text-med-muted">Recibos pendientes</span>

                    <span className="font-semibold text-med-text">
                      {m.recibos_pendientes}
                    </span>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <span className="text-med-muted">Importe pendiente</span>

                    <span className="font-semibold text-red-500">
                      {formatEUR(Number(m.importe_pendiente))}
                    </span>
                  </div>
                </div>

                <button
                  onClick={() => {
                    setSelectedMandato(m);

                    setPagoModal(true);
                  }}
                  className="mt-5 w-full rounded-xl bg-med-primary px-4 py-2 text-sm font-semibold text-white hover:bg-med-primary-hover"
                >
                  Registrar pago
                </button>

                <button
                  onClick={() => openHistorial(m)}
                  className="mt-2 w-full rounded-xl border border-med-border px-4 py-2 text-sm font-semibold text-med-text hover:bg-med-bg"
                >
                  Ver historial
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {modalGen && (
        <div
          className="fixed inset-0 bg-med-text/40 backdrop-blur-sm grid place-items-center z-50 p-4"
          onClick={() => setModalGen(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-med-surface rounded-2xl w-full max-w-lg overflow-hidden"
          >
            <div className="px-6 py-4 border-b border-med-border">
              <h3 className="font-display text-xl font-bold">
                Generar recibos del mes
              </h3>

              <p className="text-sm text-med-muted">
                Genera recibos para el mes seleccionado.
              </p>
            </div>

            <div className="p-6 space-y-5">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                    Mes
                  </label>

                  <select
                    value={modalGen.mes}
                    onChange={(e) =>
                      setModalGen({
                        ...modalGen,
                        mes: Number(e.target.value),
                      })
                    }
                    className="w-full rounded-xl bg-med-surface2 border border-transparent focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none"
                  >
                    {MESES_ES.map((m, i) => (
                      <option key={i} value={i + 1}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                    Año
                  </label>

                  <input
                    type="number"
                    value={modalGen.anio}
                    onChange={(e) =>
                      setModalGen({
                        ...modalGen,
                        anio: Number(e.target.value),
                      })
                    }
                    className="w-full rounded-xl bg-med-surface2 border border-transparent focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none"
                  />
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-med-border flex justify-end gap-3 bg-med-surface">
              <button
                onClick={() => setModalGen(null)}
                className="px-5 py-2.5 rounded-xl border-2 border-med-border text-med-text hover:border-med-primary hover:text-med-primary font-medium"
              >
                Cancelar
              </button>

              <button
                onClick={confirmGenerarMes}
                className="px-5 py-2.5 rounded-xl bg-med-primary hover:bg-med-primary-hover text-white font-semibold"
              >
                Generar recibos
              </button>
            </div>
          </div>
        </div>
      )}

      {modal && (
        <div
          className="fixed inset-0 bg-med-text/40 backdrop-blur-sm grid place-items-center z-50 p-4"
          onClick={() => setModal(null)}
          data-testid="recibo-modal"
        >
          <form
            onSubmit={onSubmit}
            onClick={(e) => e.stopPropagation()}
            className="bg-med-surface rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
          >
            <div className="px-6 py-4 border-b border-med-border flex items-center justify-between sticky top-0 bg-med-surface">
              <h3 className="font-display text-xl font-bold">
                {modal.isEdit ? "Editar recibo" : "Nuevo recibo"}
              </h3>
              <button
                type="button"
                onClick={() => setModal(null)}
                className="p-1.5 rounded-lg hover:bg-med-surface2"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                    Fallero <span className="text-med-primary">*</span>
                  </label>
                  <select
                    required
                    disabled={modal.isEdit}
                    value={modal.data.fallero_id}
                    onChange={(e) => onChangeFallero(e.target.value)}
                    data-testid="recibo-fallero"
                    className="w-full rounded-xl bg-med-surface2 border border-transparent focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none disabled:opacity-60"
                  >
                    <option value="">— Selecciona —</option>
                    {falleros.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.nombre} {f.apellidos} · {f.categoria}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                    Fecha de pago <span className="text-med-primary">*</span>
                  </label>
                  <input
                    type="date"
                    required
                    value={modal.data.fecha_pago}
                    onChange={(e) => onChangeFechaPago(e.target.value)}
                    data-testid="recibo-fecha-pago"
                    className="w-full rounded-xl bg-med-surface2 border border-transparent focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                    Estado
                  </label>
                  <select
                    value={modal.data.estado}
                    onChange={(e) =>
                      setModal({
                        ...modal,
                        data: { ...modal.data, estado: e.target.value },
                      })
                    }
                    className="w-full rounded-xl bg-med-surface2 border border-transparent focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none"
                  >
                    <option value="pendiente">Pendiente</option>
                    <option value="pagado">Pagado</option>
                    <option value="anulado">Anulado</option>
                  </select>
                </div>
              </div>

              {/* Conceptos */}
              <div className="border-t border-med-border pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-display text-lg font-bold text-med-text">
                    Conceptos
                  </h4>
                  <div className="flex gap-2">
                    {modal.data.fallero_id && !modal.isEdit && (
                      <button
                        type="button"
                        onClick={recomputeDefaults}
                        data-testid="recompute-defaults"
                        className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-med-border text-med-text hover:border-med-secondary"
                      >
                        <ArrowsClockwise size={14} weight="duotone" />
                        Recargar por defecto
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={addConcepto}
                      data-testid="add-concepto"
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-med-secondary/10 text-med-secondary hover:bg-med-secondary/20"
                    >
                      <Plus size={14} weight="bold" />
                      Añadir concepto
                    </button>
                  </div>
                </div>

                {modal.data.conceptos.length === 0 ? (
                  <div className="text-sm text-med-muted bg-med-surface2 rounded-xl p-4 text-center">
                    {modal.data.fallero_id
                      ? "No hay conceptos. Pulsa 'Añadir concepto' para empezar."
                      : "Selecciona un fallero para autocargar conceptos por defecto."}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {modal.data.conceptos.map((c, i) => (
                      <div
                        key={i}
                        data-testid={`concepto-row-${i}`}
                        className="grid grid-cols-12 gap-2 items-center bg-med-surface2/60 rounded-xl p-2"
                      >
                        <select
                          value={c.tipo}
                          onChange={(e) =>
                            updateConcepto(i, "tipo", e.target.value)
                          }
                          data-testid={`concepto-tipo-${i}`}
                          className="col-span-5 rounded-lg bg-white border border-med-border px-3 py-2 text-sm outline-none focus:border-med-primary"
                        >
                          {TIPOS.map((t) => (
                            <option key={t} value={t}>
                              {CONCEPTO_LABEL[t]}
                            </option>
                          ))}
                        </select>
                        <input
                          value={c.descripcion || ""}
                          onChange={(e) =>
                            updateConcepto(i, "descripcion", e.target.value)
                          }
                          placeholder="Descripción (opcional)"
                          className="col-span-4 rounded-lg bg-white border border-med-border px-3 py-2 text-sm outline-none focus:border-med-primary"
                        />
                        <div className="col-span-2 relative">
                          <input
                            type="number"
                            step="0.01"
                            value={c.importe}
                            onChange={(e) =>
                              updateConcepto(i, "importe", e.target.value)
                            }
                            data-testid={`concepto-importe-${i}`}
                            className="w-full rounded-lg bg-white border border-med-border px-3 py-2 text-sm text-right font-semibold outline-none focus:border-med-primary"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-med-muted pointer-events-none hidden">
                            €
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeConcepto(i)}
                          data-testid={`concepto-remove-${i}`}
                          className="col-span-1 p-2 rounded-lg text-med-danger hover:bg-med-danger/10"
                        >
                          <Trash size={16} weight="duotone" />
                        </button>
                      </div>
                    ))}
                    <div className="flex justify-end items-center gap-3 pt-2 px-2">
                      <span className="text-xs uppercase tracking-[0.12em] text-med-muted font-semibold">
                        Total
                      </span>
                      <span
                        data-testid="recibo-total"
                        className="font-display text-2xl font-bold text-med-primary"
                      >
                        {formatEUR(totalConceptos(modal.data.conceptos))}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                  Notas
                </label>
                <textarea
                  rows={2}
                  value={modal.data.notas || ""}
                  onChange={(e) =>
                    setModal({
                      ...modal,
                      data: { ...modal.data, notas: e.target.value },
                    })
                  }
                  className="w-full rounded-xl bg-med-surface2 border border-transparent focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-med-border flex justify-end gap-3 sticky bottom-0 bg-med-surface">
              <button
                type="button"
                onClick={() => setModal(null)}
                className="px-5 py-2.5 rounded-xl border-2 border-med-border text-med-text hover:border-med-primary hover:text-med-primary transition-colors font-medium"
              >
                Cancelar
              </button>
              <button
                type="submit"
                data-testid="save-recibo-btn"
                className="px-5 py-2.5 rounded-xl bg-med-primary hover:bg-med-primary-hover text-white font-semibold"
              >
                {modal.isEdit ? "Guardar" : "Crear"}
              </button>
            </div>
          </form>
        </div>
      )}

      {pagoModal && selectedMandato && (
        <div
          className="fixed inset-0 bg-med-text/40 backdrop-blur-sm grid place-items-center z-50 p-4"
          onClick={() => setPagoModal(false)}
        >
          <form
            onSubmit={submitPagoManual}
            onClick={(e) => e.stopPropagation()}
            className="bg-med-surface rounded-2xl w-full max-w-lg"
          >
            <div className="px-6 py-4 border-b border-med-border flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-wide text-med-muted">
                  Pago manual
                </div>

                <h3 className="mt-1 text-xl font-bold text-med-text">
                  {selectedMandato.mandatario}
                </h3>
              </div>

              <button
                type="button"
                onClick={() => setPagoModal(false)}
                className="p-2 rounded-lg hover:bg-med-surface2"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div className="rounded-xl bg-med-bg px-4 py-3">
                <div className="text-xs uppercase tracking-wide text-med-muted">
                  Saldo actual
                </div>

                <div className="mt-1 text-2xl font-bold text-med-text">
                  {formatEUR(Number(selectedMandato.saldo))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                  Importe
                </label>

                <input
                  type="number"
                  step="0.01"
                  required
                  value={pagoForm.importe}
                  onChange={(e) =>
                    setPagoForm({
                      ...pagoForm,

                      importe: e.target.value,
                    })
                  }
                  className="w-full rounded-xl bg-med-surface2 border border-transparent focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                  Fecha
                </label>

                <input
                  type="date"
                  required
                  value={pagoForm.fecha}
                  onChange={(e) =>
                    setPagoForm({
                      ...pagoForm,

                      fecha: e.target.value,
                    })
                  }
                  className="w-full rounded-xl bg-med-surface2 border border-transparent focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                  Notas
                </label>

                <textarea
                  rows={3}
                  value={pagoForm.notas}
                  onChange={(e) =>
                    setPagoForm({
                      ...pagoForm,

                      notas: e.target.value,
                    })
                  }
                  className="w-full rounded-xl bg-med-surface2 border border-transparent focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none"
                />
              </div>
            </div>

            <div className="px-6 py-4 border-t border-med-border flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setPagoModal(false)}
                className="px-5 py-2.5 rounded-xl border border-med-border text-med-text hover:bg-med-surface2"
              >
                Cancelar
              </button>

              <button
                type="submit"
                className="px-5 py-2.5 rounded-xl bg-med-primary hover:bg-med-primary-hover text-white font-semibold"
              >
                Registrar pago
              </button>
            </div>
          </form>
        </div>
      )}

      {historialModal && selectedMandato && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 grid place-items-center p-4"
          onClick={() => setHistorialModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-2xl rounded-2xl bg-med-surface border border-med-border overflow-hidden"
          >
            <div className="border-b border-med-border px-6 py-4 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-wide text-med-muted">
                  Historial financiero
                </div>

                <h3 className="mt-1 text-xl font-bold text-med-text">
                  {selectedMandato.mandatario}
                </h3>
              </div>

              <button
                onClick={() => setHistorialModal(false)}
                className="p-2 rounded-lg hover:bg-med-bg"
              >
                <X size={18} />
              </button>
            </div>

            <div className="max-h-[70vh] overflow-y-auto p-6">
              {loadingHistorial ? (
                <div className="text-center text-med-muted py-10">
                  Cargando...
                </div>
              ) : historial.length === 0 ? (
                <div className="text-center text-med-muted py-10">
                  No hay movimientos
                </div>
              ) : (
                <div className="space-y-4">
                  {historial.map((item, idx) => (
                    <div
                      key={idx}
                      className="flex items-start justify-between gap-4 rounded-xl border border-med-border bg-med-bg p-4"
                    >
                      <div>
                        <div
                          className={`text-sm font-semibold ${
                            item.type === "ingreso"
                              ? "text-green-600"
                              : "text-med-text"
                          }`}
                        >
                          {item.type === "ingreso"
                            ? "Ingreso manual"
                            : "Aplicado a recibo de " + item.fallero_nombre}
                        </div>

                        <div className="mt-1 text-sm text-med-muted">
                          {item.type === "ingreso"
                            ? item.notas || "Sin notas"
                            : item.concepto}
                        </div>

                        <div className="mt-2 text-xs text-med-muted">
                          {item.fecha?.slice(0, 10)}
                        </div>
                      </div>

                      <div
                        className={`text-lg font-bold ${
                          item.type === "ingreso"
                            ? "text-green-600"
                            : "text-red-500"
                        }`}
                      >
                        {item.type === "ingreso" ? "+" : "-"}

                        {formatEUR(Number(item.importe))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
